import styled, { css } from "styled-components";
import { borderRadius, mediaQueries, shadows, spacing, typographyMixin, zIndex } from "@magicbi/ui-library";
import { borderRadius as exactRadius } from "../../../design-system/tokens/borders";
import { fontSizes, fontWeights, lineHeights } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing as exactSpacing } from "../../../design-system/tokens/spacing";
import { dialogMotion, scrimMotion } from "../../../design-system/tokens/overlayMotion";
import type { DialogShellChrome, DialogShellRadius, DialogShellSize } from "./DialogShell.types";

const WIDTH: Record<DialogShellSize, string> = { sm: "32.5rem", md: "40rem", lg: "62.5rem", xl: "80rem", wide: "47.5rem" };

const WIDE_RADIUS = exactRadius[28];

/** GAP: the app top bar is 52px in the design; no layout token exposes it. */
const TOP_BAR_HEIGHT = "3.25rem";
/** F13: the dialog sits 40px below the top bar and 48px above the bottom edge. */
const DESIGN_TOP_GAP = "2.5rem";
const DESIGN_BOTTOM_GAP = "3rem";
/** F13: a 1160 by 760 dialog inset in the page (the xl shell stays 1280 wide for other callers). */
const DESIGN_XL_WIDTH = "72.5rem";
const DESIGN_XL_HEIGHT = "47.5rem";

export const Root = styled.div<{ $chrome?: DialogShellChrome }>`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[6]};
  ${({ $chrome }) =>
    $chrome === "design" &&
    css`
      padding: calc(${TOP_BAR_HEIGHT} + ${DESIGN_TOP_GAP}) ${spacing[6]} ${DESIGN_BOTTOM_GAP};
    `}

  ${mediaQueries.down.sm} {
    padding: ${spacing[3]};
  }
`;

export const Scrim = styled.div<{ $chrome?: DialogShellChrome; $closing?: boolean }>`
  position: absolute;
  inset: ${({ $chrome }) => ($chrome === "design" ? `${TOP_BAR_HEIGHT} 0 0 0` : "0")};
  background: ${({ theme }) => theme.colors.surface.overlay};
  ${({ $closing }) => scrimMotion($closing)}
`;

const RADIUS: Record<DialogShellRadius, string> = { xl: borderRadius.xl, "14": borderRadius[14] };

export const Dialog = styled.div<{
  $size: DialogShellSize;
  $width?: string;
  $radius: DialogShellRadius;
  $chrome?: DialogShellChrome;
  $closing?: boolean;
}>`
  position: relative;
  ${({ $closing }) => dialogMotion($closing)}
  display: flex;
  flex-direction: column;
  width: min(${({ $size, $width }) => $width ?? WIDTH[$size]}, 100%);
  max-height: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ $radius }) => RADIUS[$radius]};
  box-shadow: ${shadows.component.modal};
  ${({ $chrome }) =>
    $chrome === "design" &&
    css`
      border-radius: ${exactRadius[20]};
    `}
  ${({ $size }) =>
    $size === "wide" &&
    css`
      border-radius: ${WIDE_RADIUS};
    `}
  ${({ $size }) =>
    $size === "xl" &&
    css`
      height: 100%;
    `}
  ${({ $size, $chrome }) =>
    $size === "xl" &&
    $chrome === "design" &&
    css`
      width: min(${DESIGN_XL_WIDTH}, 100%);
      height: min(${DESIGN_XL_HEIGHT}, 100%);
    `}
`;

export const Header = styled.div<{ $divider: boolean; $centered?: boolean; $chrome?: DialogShellChrome; $bare?: boolean }>`
  display: flex;
  align-items: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[4]} ${spacing[3]} ${spacing[5]};
  ${({ $divider, theme }) => ($divider ? `border-bottom: 1px solid ${theme.colors.border.stroke};` : "")}
  ${({ $chrome }) =>
    $chrome === "design" &&
    css`
      align-items: center;
      min-height: ${exactSpacing[16]};
      padding: 0 ${exactSpacing[4]} 0 ${exactSpacing[6]};
    `}
  ${({ $bare }) =>
    $bare &&
    css`
      border-bottom: 0;
    `}
`;

/** Wraps the close button for the filled opt-in: 36px round, soft fill. */
export const CloseSlot = styled.span`
  display: inline-flex;
  flex: none;
  button {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: ${rawColors.grey[100]};
  }
`;

export const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

/** Between the title and the end actions; sits in the middle of the header. */
export const HeaderCenter = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  min-width: 0;
`;

export const HeaderEnd = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${spacing[2]};
`;

/** A short line above the title. */
export const Kicker = styled.p`
  margin: 0;
  ${typographyMixin("caption", 2)}
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** A kicker given as a node (a badge): its own row above the title, left as the caller styled it. */
export const KickerNode = styled.div`
  display: flex;
  margin-bottom: ${spacing[2]};
`;

export const Title = styled.h2<{ $chrome?: DialogShellChrome; $large?: boolean }>`
  margin: 0;
  ${typographyMixin("heading", "h4")}
  ${({ $chrome }) =>
    $chrome === "design" &&
    css`
      font-size: ${fontSizes.base};
      font-weight: ${fontWeights.bold};
      line-height: ${lineHeights["24"]};
    `}
  ${({ $large }) =>
    $large &&
    css`
      font-size: ${fontSizes["22"]};
      line-height: ${lineHeights["30"]};
    `}
  overflow-wrap: anywhere;
  &:focus {
    outline: none;
  }
`;

/** A div, so a description can hold more than text. */
export const Description = styled.div`
  margin-top: ${spacing[0.5]};
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[5]};
  background: ${({ theme }) => theme.colors.surface.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;

export const Body = styled.div<{ $flush?: boolean }>`
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;
  gap: ${spacing[4]};
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: ${spacing[4]} ${spacing[5]} ${spacing[5]};
  ${({ $flush }) =>
    $flush &&
    css`
      display: flex;
      gap: 0;
      overflow: hidden;
      padding: 0;
    `}
`;

export const Footer = styled.div<{ $tinted?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[5]};
  background: ${({ theme }) => theme.colors.surface.primary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};
  ${({ $tinted }) =>
    $tinted &&
    css`
      flex-wrap: nowrap;
      padding: ${exactSpacing[4]} 1.875rem;
      background: ${rawColors.structural[50]};
      > :first-child {
        flex: 1 1 auto;
      }
      /* The actions keep one row; the text beside them wraps instead. */
      > :last-child:not(:first-child) {
        flex: none;
        flex-wrap: nowrap;
      }
    `}
`;

export const FooterStart = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const FooterEnd = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing[2]};
  margin-left: auto;
`;
